import React from "react";
import { useTranslation } from "react-i18next";
import { Parcela } from "../../../services/ConsultarService";
import Format from "../../../helpers/Format";
import Button from "../../Button";
import Modal from "../Modal";
import style from "./styles.module.scss";

interface Props {
  open: boolean;
  loading: boolean;
  inCash?: boolean;
  hasPreviousBillet: boolean;
  showOverdueWarning?: boolean;
  overdueDueDate?: string;
  overdueInstallments?: Parcela[];
  paymentRequestNumber?: string;
  selectedNumbers?: number[];
  error?: string;
  onClose(): void;
  onConfirm(): void;
  onGenerateOverdue?(installment: Parcela): void;
}

const keyI18n = "translations.billetConfirmation";

export default function BilletConfirmationModal({
  open, loading, inCash = false, hasPreviousBillet, showOverdueWarning = false,
  overdueDueDate, overdueInstallments = [], paymentRequestNumber, selectedNumbers = [],
  error, onClose, onConfirm, onGenerateOverdue,
}: Props) {
  const { t } = useTranslation();
  return (
    <Modal open={open} onClose={onClose} size="medium" icon="warning"
      title={t(`${keyI18n}.${inCash ? "cashTitle" : "installmentTitle"}`)}
      dismissible={!loading}
      closeLabel={loading ? undefined : t(`${keyI18n}.close`)}
      actions={<>
        <Button variant="outlined" size="small" disabled={loading} onClick={onClose}>
          {t(`${keyI18n}.cancel`)}
        </Button>
        <Button variant="filled" size="small" disabled={loading} onClick={onConfirm}>
          {t(`${keyI18n}.${loading ? "generating" : "confirm"}`)}
        </Button>
      </>}
    >
      {error && <p role="alert" className={`pgm-body-medium ${style.error}`}>{error}</p>}
      {hasPreviousBillet && <p className="pgm-body-large">{t(`${keyI18n}.${inCash ? "cashWarning" : "installmentWarning"}`)}</p>}
      {showOverdueWarning && <p className="pgm-body-large">{t(`${keyI18n}.${overdueDueDate ? "overdueWithDate" : "overdue"}`, { date: overdueDueDate })}</p>}
      {overdueInstallments.length > 0 && <>
        <p className="pgm-body-large">{t(`${keyI18n}.missingOverdue`, { number: paymentRequestNumber })}</p>
        <ul className="pgm-body-medium">
          <li>{t(`${keyI18n}.overdue90Days`)}</li>
          <li>{t(`${keyI18n}.overdueThree`)}</li>
          <li>{t(`${keyI18n}.overdueLast`)}</li>
        </ul>
        <p className="pgm-body-medium">{t(`${keyI18n}.payOverdueFirst`)}</p>
        <ul className={style.installments}>
          {overdueInstallments.map((installment) => <li key={installment.installment_number}>
            <span className="pgm-body-medium">{t(`${keyI18n}.installmentDetails`, {
              number: installment.installment_number, value: Format.currency(installment.total), date: Format.date(installment.due_date),
            })}</span>
            <Button variant="outlined" size="small" disabled={loading} onClick={() => onGenerateOverdue?.(installment)}>
              {t(`${keyI18n}.generateInstallment`, { number: installment.installment_number })}
            </Button>
          </li>)}
        </ul>
        <p className="pgm-body-large">{t(`${keyI18n}.confirmSelected`, { numbers: selectedNumbers.join(", ") })}</p>
      </>}
    </Modal>
  );
}
